import React, { useState, useEffect, useRef } from "react";
import styled from "styled-components";
// import common components
import Typography, { TYPE_ELEMENTS } from "./Typography";
import StandardInputBox from "./form-inputs/StandardInputBox";
import Checkbox from "./form-inputs/Checkbox";
import { FormCTA } from "./CTAButton";
// import common styles
import { ModalOverlay } from "../common-styles/ModalStyles";
// import constants
import CONSTANTS from "../utils/constants/constants";
// import icons
import arrowLeft from "../assets/icons/arrow-left.svg";
// import services
import API_ENDPOINTS from "../services/api-endpoints";
import callPublicEndpoint from "../services/message.service";

const { SUB_HEADER, FORM, TERMS, CTA } = CONSTANTS.BROCHURE_MODAL;

const ModalContainer = styled.div`
  position: relative;
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 24px;
  width: 80vw;
  max-width: 700px;
  height: 70vh;
  padding: 32px;
  box-sizing: border-box;
  background: #000;
  border: 1px solid #fff;
  border-radius: 2px;
  color: #fff;
  text-align: center;

  overflow: scroll;

  scrollbar-width: none; /* Firefox */
  -ms-overflow-style: none; /* Internet Explorer 10+ */

  /* Hide scrollbar for WebKit (Chrome, Safari, Opera) */
  &::-webkit-scrollbar {
    display: none;
  }

  @media (min-width: 481px) and (max-width: 1400px) and (hover: none) and (pointer: coarse) { // for tablets
    max-width: 580px;
    height: 680px;
  }

  @media (max-width: 480px) and (orientation: portrait), (max-height: 480px) and (orientation: landscape) { // for phones
    width: 100vw;
    height: 100vh;
  }
`;

const FormContainer = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 18px;
  width: 100%;
  height: 100%;
  text-align: left;
`;

const FormRow = styled.div`
  display: flex;
  flex-direction: row;
  align-items: center;
  gap: 16px;
  width: 100%;

  @media (max-width: 1024px) {
    flex-direction: column;
    align-items: flex-start;
    gap: 12px;
  }
`;

const BackButtonWrapper = styled.div`
  display: none;

  @media (max-width: 480px) {
    display: flex;
    align-items: flex-start;
    justify-content: flex-start;
    width: 100%;
    height: fit-content;
  }

  @media (max-height: 768px) and (orientation: landscape) {
    display: flex;
    align-items: flex-start;
    justify-content: flex-start;
    width: 100%;
    height: fit-content;
  }
`;

const CloseButton = styled.div`
  position: absolute;
  top: 0px;
  right: 0px;
  display: flex;
  align-items: center;
  justify-content: center;
  background: red;
  color: white;
  padding: 2px 12px;
  font-size: 32px;
  cursor: pointer;
  border-radius: 0px 0px 0px 4px;

  @media (max-width: 480px) {
    display: none;
  }

  @media (max-height: 768px) and (orientation: landscape) {
    display: none;
  }
`;

const FORM_FIELDS = {
  FIRST_NAME: "firstName",
  LAST_NAME: "lastName",
  PHONE: "phone",
  EMAIL: "email",
  ORGANISATION: "organisation",
};

export default function BrochureModal({
  header,
  onClose = () => {},
  items = [],
}) {
  const [formData, setFormData] = useState({
    [FORM_FIELDS.FIRST_NAME]: "",
    [FORM_FIELDS.LAST_NAME]: "",
    [FORM_FIELDS.PHONE]: "",
    [FORM_FIELDS.EMAIL]: "",
    [FORM_FIELDS.ORGANISATION]: "",
  });
  const [isTermChecked, setIsTermChecked] = useState(false);
  const [processing, setProcessing] = useState(false);

  const modalRef = useRef(null);

  /**
   * curring to create a reusable function for all input changes
   */
  const handleInputChange = (fieldName) => (value) => {
    setFormData((prevData) => ({
      ...prevData,
      [fieldName]: value,
    }));
  };

  const handleSubmit = () => {
    const emptyFields = [];

    if (formData[FORM_FIELDS.FIRST_NAME].trim() === "") {
      emptyFields.push(FORM.FIRST_NAME.LABEL);
    }

    if (formData[FORM_FIELDS.LAST_NAME].trim() === "") {
      emptyFields.push(FORM.LAST_NAME.LABEL);
    }

    if (formData[FORM_FIELDS.PHONE].trim() === "") {
      emptyFields.push(FORM.PHONE.LABEL);
    }

    if (formData[FORM_FIELDS.EMAIL].trim() === "") {
      emptyFields.push(FORM.EMAIL.LABEL);
    }

    if (emptyFields.length > 0) {
      alert(`Please fill the following fields: ${emptyFields.join(", ")}`);
    } //else if (!isTermChecked) {
      //alert("Please accept the terms and conditions to submit the form");} 
    else {
      const data = {
        name: `${formData[FORM_FIELDS.FIRST_NAME]} ${
          formData[FORM_FIELDS.LAST_NAME]
        }`,
        email: formData[FORM_FIELDS.EMAIL],
        phone: formData[FORM_FIELDS.PHONE],
        organization: formData[FORM_FIELDS.ORGANISATION],
      };

      setProcessing(true);

      callPublicEndpoint(API_ENDPOINTS.people, "POST", data)
        .then((response) => {
          const id = response.id;

          callPublicEndpoint(API_ENDPOINTS.email, "POST", {
            correspondent_id: id,
            brochures: items,
          })
            .then((response) => {
              setProcessing(false);
              alert(
                "Thank you! The " + items[0] + " brochure has been sent to your email. Please check your inbox."
              );
              onClose();
            })
            .catch((error) => {
              console.error("error processing form: ", error);
              alert(
                "Sorry, we encountered an error while submitting your response. Please try again in some time."
              );
              onClose();
            });
        })
        .catch((error) => {
          console.error("error processing form: ", error);
          alert(
            "Sorry, we encountered an error while submitting your response. Please try again in some time."
          );
          onClose();
        });
    }
  };

  useEffect(() => {
    const handleClickOutside = (event) => {
      if (modalRef.current && !modalRef.current.contains(event.target)) {
        onClose();
      }
    };

    document.addEventListener("mousedown", handleClickOutside);
    return () => {
      document.removeEventListener("mousedown", handleClickOutside);
    };
  }, [onClose]);

  return (
    <ModalOverlay>
      <ModalContainer ref={modalRef}>
        <CloseButton onClick={onClose} className="close-button">
          &times;
        </CloseButton>
        <BackButtonWrapper>
          <img src={arrowLeft} onClick={onClose} />
        </BackButtonWrapper>
        <Typography typeElement={TYPE_ELEMENTS.H_5}>
          GET THE {header} BROCHURE
        </Typography>
        <Typography typeElement={TYPE_ELEMENTS.BODY_3}>{SUB_HEADER}</Typography>
        <FormContainer>
          <FormRow>
            <StandardInputBox
              label={FORM.FIRST_NAME.LABEL}
              mandatory={FORM.FIRST_NAME.MANDATORY}
              onInput={handleInputChange(FORM_FIELDS.FIRST_NAME)}
            />
            <StandardInputBox
              label={FORM.LAST_NAME.LABEL}
              mandatory={FORM.LAST_NAME.MANDATORY}
              onInput={handleInputChange(FORM_FIELDS.LAST_NAME)}
            />
          </FormRow>
          <FormRow>
            <StandardInputBox
              label={FORM.PHONE.LABEL}
              mandatory={FORM.PHONE.MANDATORY}
              onInput={handleInputChange(FORM_FIELDS.PHONE)}
            />
            <StandardInputBox
              label={FORM.EMAIL.LABEL}
              mandatory={FORM.EMAIL.MANDATORY}
              onInput={handleInputChange(FORM_FIELDS.EMAIL)}
            />
          </FormRow>
          <FormRow>
            <StandardInputBox
              label={FORM.ORGANISATION.LABEL}
              mandatory={FORM.ORGANISATION.MANDATORY}
              onInput={handleInputChange(FORM_FIELDS.ORGANISATION)}
            />
          </FormRow>
        </FormContainer>
        {/* <Checkbox
          label={TERMS}
          isPrivacyPolicy={true}
          onSelect={() => setIsTermChecked(!isTermChecked)}
        /> */}
        <FormCTA text={processing ? "Please wait..." : CTA} onClick={handleSubmit} />
      </ModalContainer>
    </ModalOverlay>
  );
}
